import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Wifi, ListOrdered, Briefcase, User, List, LayoutDashboard } from "lucide-react";
import { TopBar } from "./TopBar";
import { Watchlist } from "./Watchlist";
import { SearchDialog } from "./SearchDialog";
import { OrderWindow } from "./OrderWindow";
import { BasketPanel } from "./BasketPanel";
import { GooeyConnect, FirstFillBadge } from "./motion";
import { GttDialog } from "./GttDialog";
import { DisclosureGate } from "./SimBadge";
import { TgWidget, TgChip, useCommunityInfo } from "./community/TgBits";
import { TradePrompts } from "./community/TradePrompts";
import { ShareSheet } from "./community/ShareSheet";
import { useBasket } from "../lib/basket";
import { useMarket } from "../lib/store";
import { useTheme } from "../lib/theme";
import { useAuth } from "../context/AuthContext";

const TABS = [["/", "Dashboard"], ["/orders", "Orders"], ["/positions", "Positions"], ["/holdings", "Holdings"], ["/funds", "Funds"], ["/option-chain", "Option Chain"], ["/strategy", "Strategy"], ["/futures", "Futures"], ["/gtt", "GTT & Alerts"], ["/reports", "Reports"]];

function PlatformBanner() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => api.get("/platform/banner").then((r) => setItems(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  return items.map((b, i) => (
    <div key={i} data-testid={`platform-banner-${b.level}`} className={`border-b text-[11.5px] py-1 px-3 flex items-center gap-2 ${b.level === "error" ? "bg-down/10 text-down border-down/20" : "bg-primary/10 text-primary border-primary/20"}`}>
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />{b.text}
    </div>
  ));
}

function ConnectBanner() {
  const status = useMarket((s) => s.status);
  const wsState = useMarket((s) => s.wsState);
  if (wsState === "closed") {
    return <div data-testid="ws-disconnected-banner" className="bg-down/10 text-down border-b border-down/20 text-[11.5px] py-1 px-3 flex items-center gap-2"><Wifi className="h-3.5 w-3.5" />Connection to the trading server lost — reconnecting…</div>;
  }
  if (!status || status.health === "live") return null;
  if (status.health === "delayed") {
    return <div data-testid="feed-delayed-banner" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-b border-amber-500/20 text-[11.5px] py-1 px-3 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />Live stream reconnecting — prices are delayed and refresh every few seconds.</div>;
  }
  return (
    <div data-testid="feed-unavailable-banner" className="bg-down/10 text-down border-b border-down/25 text-[11.5px] py-1 px-3 flex items-start gap-2">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <span><b>Live market data unavailable — trading paused.</b> New orders are blocked; you can still cancel open orders. Prices shown are the last known values.</span>
    </div>
  );
}

function useShortcuts() {
  const nav = useNavigate();
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      const typing = ["input", "textarea", "select"].includes(tag) || e.target.isContentEditable;
      const s = useMarket.getState();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); s.setSearch(true); return; }
      if (e.key === "Escape") { s.closeOrder(); return; }
      if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "/") { e.preventDefault(); s.setSearch(true); }
      else if ((e.key === "b" || e.key === "B") && s.focusKey) s.openOrder({ key: s.focusKey, side: "BUY" });
      else if ((e.key === "s" || e.key === "S") && s.focusKey) s.openOrder({ key: s.focusKey, side: "SELL" });
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [nav]);
}

function MobileNav() {
  const items = [["/", "Dashboard", LayoutDashboard], ["/watchlist", "Watchlist", List], ["/orders", "Orders", ListOrdered], ["/positions", "Positions", Briefcase], ["/profile", "Profile", User]];
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 h-14 bg-card border-t border-border flex z-50" data-testid="mobile-bottom-nav">
      {items.map(([to, label, Icon]) => (
        <NavLink key={to} to={to} end={to === "/"} data-testid={`mobile-nav-${label.toLowerCase()}`}
          className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] ${isActive ? "text-primary" : "text-muted-foreground"}`}>
          <Icon className="h-4 w-4" />{label}
        </NavLink>
      ))}
    </nav>
  );
}

function BasketFab() {
  const { legs, setOpen } = useBasket();
  if (!legs.length) return null;
  return (
    <button data-testid="basket-fab" onClick={() => setOpen(true)}
      className="fixed z-50 bottom-20 md:bottom-6 right-6 h-10 px-4 rounded-full bg-gold text-[#0b0b0d] font-semibold text-[12px] shadow-gold hover:brightness-110 page-in">
      Basket · {legs.length}
    </button>
  );
}

export default function AppShell() {
  const [theme, toggleTheme] = useTheme();
  const { user } = useAuth();
  const loc = useLocation();
  useShortcuts();
  useCommunityInfo();
  useEffect(() => { useMarket.getState().closeOrder(); }, [loc.pathname]);
  const tabs = TABS;
  return (
    <div className="h-full flex flex-col grain">
      <TopBar theme={theme} toggleTheme={toggleTheme} />
      <PlatformBanner />
      <ConnectBanner />
      <div className="flex-1 flex overflow-hidden relative z-[1]">
        <aside className="relative hidden md:flex w-[320px] lg:w-[340px] shrink-0 border-r border-border bg-card/70 backdrop-blur-xl flex-col">
          <GooeyConnect />
          <div className="flex-1 min-h-[180px]"><Watchlist /></div>
          <div className="shrink-0 p-3 border-t border-border"><TgWidget compact /></div>
        </aside>
        <main className="flex-1 flex flex-col min-w-0">
          <nav className="hidden md:flex items-center gap-0.5 px-3 h-10 border-b border-border bg-card/50 backdrop-blur-xl overflow-x-auto no-scrollbar" data-testid="main-tabs">
            {tabs.map(([to, label]) => (
              <NavLink key={to} to={to} end={to === "/"} data-testid={`tab-${label.toLowerCase().replace(/\s/g, "-").replace("&", "and")}`}
                className={({ isActive }) => `relative h-10 px-3 flex items-center text-[12.5px] transition-colors duration-200 whitespace-nowrap ${isActive ? "text-foreground font-medium after:absolute after:inset-x-2 after:bottom-0 after:h-[2px] after:rounded-t after:bg-primary after:shadow-[0_0_12px_hsl(var(--gold)/0.7)]" : "text-muted-foreground hover:text-foreground"}`}>
                {label}
              </NavLink>
            ))}
            <span className="ml-auto hidden lg:flex items-center gap-1 text-[10px] text-muted-foreground whitespace-nowrap"><LayoutDashboard className="h-3 w-3" />B buy · S sell · / search · Esc close</span>
          </nav>
          <div className="flex-1 overflow-auto thin-scroll pb-16 md:pb-0">
            <div key={loc.pathname} className="page-in min-h-full"><Outlet /></div>
          </div>
          <div className="hidden md:flex gw-statusbar" data-testid="status-bar"><span className="num">Simulated trading terminal</span><TgChip /></div>
        </main>
      </div>
      <MobileNav />
      <SearchDialog />
      <OrderWindow />
      <BasketPanel />
      <FirstFillBadge />
      <GttDialog />
      <BasketFab />
      <TradePrompts />
      <ShareSheet />
      <DisclosureGate />
    </div>
  );
}
